/**
 * Global resizable columns for all <table> elements.
 *
 * - Auto-injects a drag handle (.col-resizer) into every <thead><th>.
 * - Saves/restores column widths in localStorage per table (keyed by data-table-id
 *   or by the concatenation of header texts as a fallback).
 * - Re-runs on DOM mutations so dynamically rendered tables are covered.
 * - RTL-aware: in RTL, dragging right shrinks the column (boundary is on the
 *   visual left edge); in LTR, dragging right grows it.
 */

const LS_PREFIX = 'colw:';
const MIN_WIDTH = 60;

function tableKey(table) {
  if (table.dataset.tableId) return LS_PREFIX + table.dataset.tableId;
  const ths = table.querySelectorAll('thead th');
  const sig = Array.from(ths)
    .map(th => (th.textContent || '').trim().slice(0, 24))
    .join('|');
  return LS_PREFIX + (sig || 'unknown');
}

function loadWidths(table) {
  try {
    const raw = localStorage.getItem(tableKey(table));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveWidths(table, widths) {
  try {
    localStorage.setItem(tableKey(table), JSON.stringify(widths));
  } catch {
    /* quota exceeded — silently ignore */
  }
}

function applySavedWidths(table) {
  const widths = loadWidths(table);
  if (!widths) return;
  table.querySelectorAll('thead th').forEach((th, idx) => {
    if (widths[idx] && widths[idx] > MIN_WIDTH) {
      th.style.width = widths[idx] + 'px';
      th.style.minWidth = widths[idx] + 'px';
    }
  });
}

function isRTL(el) {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const dir = cur.getAttribute && cur.getAttribute('dir');
    if (dir) return dir === 'rtl';
    cur = cur.parentElement;
  }
  return getComputedStyle(document.documentElement).direction === 'rtl';
}

function attachResizer(th, table) {
  if (th.querySelector(':scope > .col-resizer')) return;
  const resizer = document.createElement('div');
  resizer.className = 'col-resizer';
  resizer.setAttribute('role', 'separator');
  resizer.setAttribute('aria-orientation', 'vertical');
  resizer.title = 'اسحب لتغيير عرض العمود';
  th.appendChild(resizer);

  let startX = 0;
  let startWidth = 0;
  let rtl = false;

  const onMove = (ev) => {
    const x = ev.touches ? ev.touches[0].pageX : ev.pageX;
    const delta = x - startX;
    const newWidth = Math.max(MIN_WIDTH, rtl ? startWidth - delta : startWidth + delta);
    th.style.width = newWidth + 'px';
    th.style.minWidth = newWidth + 'px';
  };

  const onEnd = () => {
    resizer.classList.remove('active');
    document.body.classList.remove('col-resizing');
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onEnd);
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
    // Persist
    const widths = {};
    table.querySelectorAll('thead th').forEach((cell, idx) => {
      widths[idx] = cell.offsetWidth;
    });
    saveWidths(table, widths);
  };

  const onStart = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    rtl = isRTL(table);
    startX = ev.touches ? ev.touches[0].pageX : ev.pageX;
    startWidth = th.offsetWidth;
    resizer.classList.add('active');
    document.body.classList.add('col-resizing');
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  resizer.addEventListener('mousedown', onStart);
  resizer.addEventListener('touchstart', onStart, { passive: false });

  // Double-click to reset this column's width
  resizer.addEventListener('dblclick', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    th.style.width = '';
    th.style.minWidth = '';
    const widths = {};
    table.querySelectorAll('thead th').forEach((cell, idx) => {
      widths[idx] = cell.offsetWidth;
    });
    saveWidths(table, widths);
  });
}

function setupTable(table) {
  if (table.dataset.resizableInit === '1') return;
  table.dataset.resizableInit = '1';
  // Allow per-column manual width to actually take effect
  if (!table.style.tableLayout) {
    // Use auto so CSS auto-fits when no explicit widths set; user widths still apply
    table.style.tableLayout = 'auto';
  }
  applySavedWidths(table);
  table.querySelectorAll('thead th').forEach(th => attachResizer(th, table));
}

let scanQueued = false;
function scan() {
  if (scanQueued) return;
  scanQueued = true;
  // Defer to next frame so freshly rendered tables are present
  requestAnimationFrame(() => {
    scanQueued = false;
    document.querySelectorAll('table').forEach(setupTable);
  });
}

let observer = null;
export function initResizableColumns() {
  if (typeof window === 'undefined') return;
  if (observer) return; // already initialized
  scan();
  observer = new MutationObserver((mutations) => {
    // Cheap pre-filter: only rescan if a <table> or rows were added
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length > 0) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) {
            if (n.tagName === 'TABLE' || n.querySelector?.('table')) {
              scan();
              return;
            }
          }
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function teardownResizableColumns() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}
